import { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useMainScrollElement } from "@/app/context/MainScrollContext";
import { ScrollCardReveal } from "@/shared/components/ScrollCardReveal";
import { layoutTokens } from "@/shared/theme";
import { principles } from "./constants";
import { PrincipleRow, TeamSection } from "./components";
import { LatestWinBoard } from "./components/LatestWinBoard";

export const AboutPage = () => {
  const mainScrollElement = useMainScrollElement();
  const revealedPrinciples = useRef(new Set<string>());

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
        px: layoutTokens.workspacePagePadding,
        overflowX: "clip",
      }}
    >
      <Box
        component="section"
        aria-labelledby="about-title"
        sx={{
          minHeight: { xs: "calc(100dvh - var(--mobile-app-bar-height, 44px))", md: "100dvh" },
          py: { xs: 5, md: 7 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1.2fr) minmax(360px,.8fr)" },
          gap: { xs: 5, lg: 6 },
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 900, letterSpacing: ".12em" }}>
            01 / ABOUT CHESSLA
          </Typography>
          <Typography
            id="about-title"
            component="h1"
            sx={{
              mt: { xs: 3, md: 5 },
              maxWidth: 1000,
              fontSize: { xs: "clamp(3.6rem,17vw,6.5rem)", md: "clamp(6rem,8.3vw,10rem)" },
              fontWeight: 950,
              lineHeight: 0.79,
              letterSpacing: "-.09em",
              textTransform: "uppercase",
            }}
          >
            YOUR GAMES
            <br />
            HAVE A VOICE.
            <br />
            <Box component="span" sx={{ color: "secondary.main", fontSize: ".43em", letterSpacing: "-.065em" }}>
              HEAR WHAT THEY&apos;RE TELLING YOU.
            </Box>
          </Typography>
          <Box
            sx={{
              mt: { xs: 4, md: 6 },
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "minmax(0,460px) auto" },
              gap: 3,
              alignItems: "end",
            }}
          >
            <Typography sx={{ color: "text.secondary", fontSize: { xs: ".85rem", md: "1rem" }, lineHeight: 1.7 }}>
              Chessla is personal chess intelligence built around your real games. It connects decisions across your
              history, then turns the signal into something useful for the next game.
            </Typography>
            <Typography
              sx={{
                color: "text.primary",
                fontSize: ".62rem",
                fontWeight: 900,
                letterSpacing: ".08em",
                whiteSpace: { sm: "nowrap" },
              }}
            >
              ONE PLAYER / MANY GAMES / A CLEARER PATTERN
            </Typography>
          </Box>
        </Box>
        <LatestWinBoard />
      </Box>

      <Box
        component="section"
        aria-labelledby="origin-title"
        sx={{ py: { xs: 8, md: 13 }, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Typography sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 900, letterSpacing: ".12em" }}>
          02 / THE FIRST POSITION
        </Typography>
        <Typography
          id="origin-title"
          component="h2"
          sx={{
            mt: { xs: 4, md: 7 },
            maxWidth: 1280,
            fontSize: { xs: "clamp(2.8rem,13vw,5rem)", md: "clamp(5rem,7.5vw,9rem)" },
            fontWeight: 950,
            lineHeight: 0.82,
            letterSpacing: "-.085em",
            textTransform: "uppercase",
          }}
        >
          THE GAMES WERE THERE.
          <br />
          <Box component="span" sx={{ color: "secondary.main" }}>
            ONE QUESTION KEPT REPEATING.
          </Box>
        </Typography>
        <Box
          sx={{
            mt: { xs: 5, md: 8 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: ".85fr 1.15fr" },
            gap: { xs: 3, md: 8 },
            alignItems: "start",
          }}
        >
          <Typography
            sx={{ fontSize: { xs: "1.8rem", md: "3rem" }, fontWeight: 950, lineHeight: 0.95, letterSpacing: "-.055em" }}
          >
            WHAT SHOULD I<br />
            WORK ON NEXT?
          </Typography>
          <Box sx={{ maxWidth: 720 }}>
            <Typography sx={{ color: "text.secondary", lineHeight: 1.8 }}>
              Reviewing one loss can explain what happened. It rarely shows what deserves attention next. The useful
              answer lives across games: the same missed forcing move, rushed decision, or position asking the same
              question.
            </Typography>
            <Typography sx={{ mt: 2, lineHeight: 1.8 }}>
              Chessla keeps that work connected: exact position, private thinking, verified consequence, recurring
              evidence, then a cue you can carry to the board.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box component="section" aria-labelledby="principles-title" sx={{ py: { xs: 8, md: 13 } }}>
        <Typography sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 900, letterSpacing: ".12em" }}>
          03 / WHAT WE KEEP
        </Typography>
        <Typography
          id="principles-title"
          component="h2"
          sx={{
            mt: { xs: 4, md: 7 },
            maxWidth: 1200,
            fontSize: { xs: "clamp(2.8rem,13vw,5rem)", md: "clamp(5rem,7vw,8rem)" },
            fontWeight: 950,
            lineHeight: 0.82,
            letterSpacing: "-.085em",
            textTransform: "uppercase",
          }}
        >
          FIND THE SIGNAL.
          <br />
          <Box component="span" sx={{ color: "secondary.main" }}>
            MAKE THE NEXT MOVE CLEARER.
          </Box>
        </Typography>
        <Box
          sx={{
            mt: { xs: 5, md: 8 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" },
            gap: 1,
          }}
        >
          {principles.map((principle, index) => {
            const row = <PrincipleRow {...principle} />;
            return mainScrollElement ? (
              <ScrollCardReveal
                key={principle.number}
                scrollRoot={mainScrollElement}
                revealKey={`about-principle-${principle.number}`}
                revealedKeys={revealedPrinciples.current}
                animationsEnabled
                exemptInitialViewport={false}
                revealInitialViewport
                animationDelayMs={120 + index * 90}
              >
                {row}
              </ScrollCardReveal>
            ) : (
              <Box key={principle.number}>{row}</Box>
            );
          })}
        </Box>
      </Box>

      <TeamSection />

      <Box
        component="section"
        sx={{
          my: { xs: 4, md: 8 },
          p: { xs: 3, sm: 5, md: 7 },
          bgcolor: "text.primary",
          color: "background.default",
          borderLeft: "8px solid",
          borderColor: "secondary.main",
        }}
      >
        <Typography sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 900, letterSpacing: ".12em" }}>
          CURIOUS PLAYERS WELCOME.
        </Typography>
        <Typography
          component="h2"
          sx={{
            mt: 3,
            fontSize: { xs: "clamp(2.8rem,13vw,5rem)", md: "clamp(5rem,7vw,8rem)" },
            fontWeight: 950,
            lineHeight: 0.82,
            letterSpacing: "-.085em",
            textTransform: "uppercase",
          }}
        >
          SEE WHAT REPEATS.
          <br />
          PLAY THE NEXT ONE WISER.
        </Typography>
        <Typography sx={{ mt: 3, maxWidth: 680, color: "background.paper", lineHeight: 1.7 }}>
          Whether you are finding your first recurring mistake or reviewing hundreds of games, Chessla keeps the signal
          clear and the work connected to the board.
        </Typography>
      </Box>
    </Box>
  );
};
